/**
 * Pure banding / interpretation for the baseline report — Phase 3 (mock).
 *
 * Turns a final survey score into a neutral wellbeing band for display. This is
 * deliberately framed as GUIDANCE, never a diagnosis (patient-safety
 * non-negotiable — see docs/platform-upgrade-2026.md § Safety). The bands here
 * are generic positions on the scale's own range, NOT clinical cut-offs for any
 * specific instrument.
 *
 * TODO[VER-108/Phase3]: replace generic thirds with the instrument-specific
 *   bands once the real scale metadata is available from the backend.
 */

export type WellbeingBand = "lower" | "moderate" | "higher";

export type BandResult = {
    band: WellbeingBand;
    /** Short neutral label, e.g. "Moderate wellbeing range". */
    label: string;
    /** One-line neutral interpretation. Always non-diagnostic. */
    interpretation: string;
    /** finalScore as a 0–100 percent of the scale's [min, max] range. */
    percentOfRange: number;
};

const clampPercent = (n: number): number => Math.max(0, Math.min(100, n));

/**
 * Band a final score against the scale's raw range.
 * Lower third → "lower", middle third → "moderate", upper third → "higher".
 *
 * @param finalScore the score after any multiplier (e.g. WHO-5 ×4)
 * @param range the survey's raw [min, max] BEFORE any multiplier
 * @param multiplier optional multiplier applied to the raw range for comparison
 * @param higherIsBetter whether a higher score means higher wellbeing (WHO-5: true;
 *   UCLA-20 loneliness: false — a higher sum is WORSE). Defaults to true.
 */
export const bandScore = (
    finalScore: number,
    range: [number, number],
    multiplier?: number,
    higherIsBetter = true,
): BandResult => {
    const mult = multiplier ?? 1;
    const min = range[0] * mult;
    const max = range[1] * mult;
    const span = max - min;

    const percentOfRange = span > 0 ? clampPercent(((finalScore - min) / span) * 100) : 0;

    // Band on WELLBEING, not the raw score. For scales where a higher score is worse
    // (e.g. UCLA loneliness), invert so a high score reads as a LOWER wellbeing band.
    const wellbeingPercent = higherIsBetter ? percentOfRange : 100 - percentOfRange;

    let band: WellbeingBand;
    if (wellbeingPercent < 33.34) {
        band = "lower";
    } else if (wellbeingPercent < 66.67) {
        band = "moderate";
    } else {
        band = "higher";
    }

    const label =
        band === "lower"
            ? "Lower wellbeing range"
            : band === "moderate"
              ? "Moderate wellbeing range"
              : "Higher wellbeing range";

    const interpretation =
        band === "lower"
            ? "This baseline sits in the lower part of the scale. It is a starting point for the pathway, not a clinical finding."
            : band === "moderate"
              ? "This baseline sits in the middle of the scale and gives a balanced reference for tracking change over the programme."
              : "This baseline sits in the higher part of the scale, a strong reference point to track against over the programme.";

    return { band, label, interpretation, percentOfRange: Math.round(percentOfRange) };
};
